// Tremor AreaChart [v1.0.0]
/* eslint-disable @typescript-eslint/no-explicit-any */

"use client";

import React from "react";
import {
  Area,
  CartesianGrid,
  Dot,
  Label,
  Line,
  AreaChart as RechartsAreaChart,
  Legend as RechartsLegend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  AvailableChartColors,
  constructCategoryColors,
  getColorClassName,
  hasOnlyOneValueForKey,
  type AvailableChartColorsKeys,
  calculateDynamicMargins,
  measureTextWidth,
} from "@/lib/chartUtils";
import {
  calculateNiceYAxisDomain,
  generateCustomTicks,
  valueToPercent,
} from "@/lib/chartUtils";
import { cn } from "@/lib/utils";

import { ChartLegend } from "./components/ChartLegend";
import { ChartTooltip, type TooltipProps } from "./components/ChartTooltip";
import { createCustomTick } from "./components/CustomTick";
//#region Legend - Using shared component

//#region Tooltip - Using shared component

//#region AreaChart

interface ActiveDot {
  index?: number;
  dataKey?: string;
}

type BaseEventProps = {
  eventType: "dot" | "category";
  categoryClicked: string;
  [key: string]: number | string;
};

type AreaChartEventProps = BaseEventProps | null | undefined;

interface AreaChartProps extends React.HTMLAttributes<HTMLDivElement> {
  data: Record<string, any>[];
  index: string;
  categories: string[];
  colors?: AvailableChartColorsKeys[];
  valueFormatter?: (value: number) => string;
  startEndOnly?: boolean;
  showXAxis?: boolean;
  showYAxis?: boolean;
  showGridLines?: boolean;
  yAxisWidth?: number;
  intervalType?: "preserveStartEnd" | "equidistantPreserveStart" | 0;
  tickCount?: number;
  showTooltip?: boolean;
  showLegend?: boolean;
  autoMinValue?: boolean;
  minValue?: number;
  maxValue?: number;
  allowDecimals?: boolean;
  onValueChange?: (value: AreaChartEventProps) => void;
  enableLegendSlider?: boolean;
  tickGap?: number;
  connectNulls?: boolean;
  xAxisLabel?: string;
  yAxisLabel?: string;
  type?: "default" | "stacked" | "percent";
  legendPosition?: "left" | "center" | "right";
  fill?: "gradient" | "solid" | "none";
  tooltipCallback?: (tooltipCallbackContent: TooltipProps) => void;
  customTooltip?: React.ComponentType<TooltipProps>;
  chartMargin?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
}

const AreaChart = React.forwardRef<HTMLDivElement, AreaChartProps>((props, ref) => {
  const {
    data = [],
    categories = [],
    index,
    colors = AvailableChartColors,
    valueFormatter = (value: number) => value.toString(),
    startEndOnly = false,
    showXAxis = true,
    showYAxis = true,
    showGridLines = true,
    yAxisWidth = 20,
    intervalType = "equidistantPreserveStart",
    showTooltip = true,
    showLegend = true,
    autoMinValue = false,
    minValue,
    maxValue,
    allowDecimals = true,
    connectNulls = false,
    className,
    onValueChange,
    enableLegendSlider = false,
    tickGap = 2,
    tickCount,
    xAxisLabel,
    yAxisLabel,
    type = "default",
    legendPosition = "right",
    fill = "gradient",
    tooltipCallback,
    customTooltip,
    chartMargin,
    ...other
  } = props;
  const CustomTooltip = customTooltip;
  const paddingValue =
    (!showXAxis && !showYAxis) || (startEndOnly && !showYAxis) ? 0 : 20;
  const [legendHeight, setLegendHeight] = React.useState(60);
  const [activeDot, setActiveDot] = React.useState<ActiveDot | undefined>(undefined);
  const [activeLegend, setActiveLegend] = React.useState<string | undefined>(undefined);
  const [shouldRotateTicks, setShouldRotateTicks] = React.useState(true);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const categoryColors = constructCategoryColors(categories, colors);

  // Calculate if labels fit horizontally based on ACTUAL rendered ticks from Recharts
  React.useEffect(() => {
    const calculateRotation = () => {
      if (!containerRef.current || !data?.length) {
        setShouldRotateTicks(false);
        return;
      }

      // Wait for DOM to be fully rendered
      // Use double RAF to ensure Recharts has finished rendering
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const svg = containerRef.current?.querySelector("svg");
          if (!svg) {
            setShouldRotateTicks(true);
            return;
          }

          // Find all x-axis tick text elements that Recharts actually rendered
          const xAxisGroup = svg.querySelector(".recharts-xAxis");
          if (!xAxisGroup) {
            setShouldRotateTicks(true);
            return;
          }

          const tickTexts = Array.from(xAxisGroup.querySelectorAll("text"));

          if (tickTexts.length === 0) {
            setShouldRotateTicks(true);
            return;
          }

          // Get the actual positions and widths of visible ticks
          const tickInfo = tickTexts
            .map((textEl) => {
              const transform = textEl.parentElement?.getAttribute("transform");
              if (!transform) return null;

              // Extract x coordinate from transform="translate(x, y)"
              const match = transform.match(/translate\(([^,]+),/);
              const x = match ? parseFloat(match[1]) : null;
              const text = textEl.textContent || "";

              if (x === null || text === "") return null;

              // Measure the actual text width
              const textWidth = measureTextWidth(text, 12);

              return { x, text, textWidth };
            })
            .filter(
              (info): info is { x: number; text: string; textWidth: number } =>
                info !== null,
            )
            .sort((a, b) => a.x - b.x); // Sort by x position

          if (tickInfo.length < 2) {
            setShouldRotateTicks(true);
            return;
          }

          // Calculate actual spacing between adjacent visible ticks
          const spacings: number[] = [];
          for (let i = 0; i < tickInfo.length - 1; i++) {
            spacings.push(tickInfo[i + 1].x - tickInfo[i].x);
          }

          const minSpacing = Math.min(...spacings);
          const maxLabelWidth = Math.max(...tickInfo.map((t) => t.textWidth));

          // Labels can fit horizontally if minimum spacing is enough for the widest label
          // Add 5px buffer for visual clarity
          const canFit = minSpacing >= maxLabelWidth + 5;

          setShouldRotateTicks(!canFit);
        });
      });
    };

    calculateRotation();

    // Recalculate on window resize
    const handleResize = () => calculateRotation();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [data, index, chartMargin, xAxisLabel, yAxisLabel, tickGap]);

  const niceYAxisDomain = calculateNiceYAxisDomain(
    data,
    categories,
    autoMinValue,
    minValue,
    maxValue,
    5,
  );
  const customTicks = generateCustomTicks(niceYAxisDomain, 5, allowDecimals);
  const hasOnValueChange = !!onValueChange;
  const stacked = type === "stacked" || type === "percent";
  const areaId = React.useId();

  const prevActiveRef = React.useRef<boolean | undefined>(undefined);
  const prevLabelRef = React.useRef<string | undefined>(undefined);

  // Create tick component with rotation state
  const CustomTickComponent = React.useMemo(
    () => createCustomTick(shouldRotateTicks),
    [shouldRotateTicks],
  );

  const getFillContent = ({
    fillType,
    activeDot,
    activeLegend,
    category,
  }: {
    fillType: AreaChartProps["fill"];
    activeDot: ActiveDot | undefined;
    activeLegend: string | undefined;
    category: string;
  }) => {
    const stopOpacity =
      activeDot || (activeLegend && activeLegend !== category) ? 0.1 : 0.3;

    switch (fillType) {
      case "none":
        return <stop stopColor="currentColor" stopOpacity={0} />;
      case "gradient":
        return (
          <>
            <stop offset="5%" stopColor="currentColor" stopOpacity={stopOpacity} />
            <stop offset="95%" stopColor="currentColor" stopOpacity={0} />
          </>
        );
      case "solid":
      default:
        return <stop stopColor="currentColor" stopOpacity={stopOpacity} />;
    }
  };

  function onDotClick(itemData: any, event: React.MouseEvent) {
    event.stopPropagation();

    if (!hasOnValueChange) return;
    if (
      (itemData.index === activeDot?.index && itemData.dataKey === activeDot?.dataKey) ||
      (hasOnlyOneValueForKey(data, itemData.dataKey) &&
        activeLegend &&
        activeLegend === itemData.dataKey)
    ) {
      setActiveLegend(undefined);
      setActiveDot(undefined);
      onValueChange?.(null);
    } else {
      setActiveLegend(itemData.dataKey);
      setActiveDot({
        index: itemData.index,
        dataKey: itemData.dataKey,
      });
      onValueChange?.({
        eventType: "dot",
        categoryClicked: itemData.dataKey,
        ...itemData.payload,
      });
    }
  }

  function onCategoryClick(dataKey: string) {
    if (!hasOnValueChange) return;
    if (
      (dataKey === activeLegend && !activeDot) ||
      (hasOnlyOneValueForKey(data, dataKey) && activeDot && activeDot.dataKey === dataKey)
    ) {
      setActiveLegend(undefined);
      onValueChange?.(null);
    } else {
      setActiveLegend(dataKey);
      onValueChange?.({
        eventType: "category",
        categoryClicked: dataKey,
      });
    }
    setActiveDot(undefined);
  }

  // Handle ref forwarding - use containerRef for measurements
  React.useEffect(() => {
    if (containerRef.current && ref) {
      if (typeof ref === "function") {
        ref(containerRef.current);
      } else {
        ref.current = containerRef.current;
      }
    }
  }, [ref]);

  return (
    <div
      ref={containerRef}
      className={cn("h-80 w-full", className)}
      tremor-id="tremor-raw"
      {...other}
    >
      <ResponsiveContainer>
        <RechartsAreaChart
          data={data}
          onClick={
            hasOnValueChange && (activeLegend || activeDot)
              ? () => {
                  setActiveDot(undefined);
                  setActiveLegend(undefined);
                  onValueChange?.(null);
                }
              : undefined
          }
          margin={calculateDynamicMargins(
            data,
            index,
            chartMargin,
            xAxisLabel,
            yAxisLabel,
          )}
          stackOffset={type === "percent" ? "expand" : undefined}
        >
          {showGridLines ? (
            <CartesianGrid
              className={cn("stroke-gray-200 stroke-1 dark:stroke-gray-800")}
              horizontal={true}
              vertical={false}
            />
          ) : null}
          <XAxis
            padding={{ left: paddingValue, right: paddingValue }}
            hide={!showXAxis}
            dataKey={index}
            interval={intervalType}
            tickCount={tickCount}
            tick={CustomTickComponent}
            fill=""
            stroke=""
            className={cn(
              // base
              "text-xs",
              // text fill
              "fill-gray-500 dark:fill-gray-500",
            )}
            tickLine={true}
            axisLine={false}
            minTickGap={tickGap}
            tickMargin={20}
          >
            {xAxisLabel && (
              <Label
                position="insideBottom"
                offset={-20}
                className="fill-gray-800 text-sm font-medium dark:fill-gray-200"
              >
                {xAxisLabel}
              </Label>
            )}
          </XAxis>
          <YAxis
            width={yAxisWidth}
            hide={!showYAxis}
            axisLine={false}
            tickLine={false}
            type="number"
            domain={niceYAxisDomain}
            ticks={customTicks}
            tick={{ transform: "translate(-3, 0)" }}
            fill=""
            stroke=""
            className={cn(
              // base
              "text-xs",
              // text fill
              "fill-gray-500 dark:fill-gray-500",
            )}
            tickFormatter={type === "percent" ? valueToPercent : valueFormatter}
            allowDecimals={allowDecimals}
          >
            {yAxisLabel && (
              <Label
                position="insideLeft"
                style={{ textAnchor: "middle" }}
                angle={-90}
                offset={-15}
                className="fill-gray-800 text-sm font-medium dark:fill-gray-200"
              >
                {yAxisLabel}
              </Label>
            )}
          </YAxis>
          <Tooltip
            wrapperStyle={{ outline: "none", zIndex: 50 }}
            isAnimationActive={true}
            animationDuration={100}
            cursor={{ stroke: "#d1d5db", strokeWidth: 1 }}
            offset={20}
            position={{ y: 0 }}
            content={({ active, payload, label }) => {
              const cleanPayload: TooltipProps["payload"] = payload
                ? payload.map((item: any) => ({
                    category: item.dataKey,
                    value: item.value,
                    index: item.payload[index],
                    color: categoryColors.get(item.dataKey) as AvailableChartColorsKeys,
                    type: item.type,
                    payload: item.payload,
                  }))
                : [];

              if (
                tooltipCallback &&
                (active !== prevActiveRef.current || label !== prevLabelRef.current)
              ) {
                tooltipCallback({ active, payload: cleanPayload, label });
                prevActiveRef.current = active;
                prevLabelRef.current = label;
              }

              return showTooltip && active ? (
                CustomTooltip ? (
                  <CustomTooltip active={active} payload={cleanPayload} label={label} />
                ) : (
                  <ChartTooltip
                    active={active}
                    payload={cleanPayload}
                    label={label}
                    valueFormatter={valueFormatter}
                  />
                )
              ) : null;
            }}
          />

          {showLegend ? (
            <RechartsLegend
              verticalAlign="top"
              height={legendHeight}
              content={({ payload }) =>
                ChartLegend(
                  { payload },
                  categoryColors,
                  setLegendHeight,
                  activeLegend,
                  hasOnValueChange
                    ? (clickedLegendItem: string) => onCategoryClick(clickedLegendItem)
                    : undefined,
                  enableLegendSlider,
                  legendPosition,
                  yAxisWidth,
                )
              }
            />
          ) : null}
          {categories.map((category) => {
            const categoryId = `${areaId}-${category.replace(/[^a-zA-Z0-9]/g, "")}`;
            return (
              <React.Fragment key={category}>
                <defs key={category}>
                  <linearGradient
                    key={category}
                    className={cn(
                      getColorClassName(
                        categoryColors.get(category) as AvailableChartColorsKeys,
                        "text",
                      ),
                    )}
                    id={categoryId}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    {getFillContent({
                      fillType: fill,
                      activeDot: activeDot,
                      activeLegend: activeLegend,
                      category: category,
                    })}
                  </linearGradient>
                </defs>
                <Area
                  className={cn(
                    getColorClassName(
                      categoryColors.get(category) as AvailableChartColorsKeys,
                      "stroke",
                    ),
                  )}
                  strokeOpacity={
                    activeDot || (activeLegend && activeLegend !== category) ? 0.3 : 1
                  }
                  activeDot={(props: any) => {
                    const {
                      cn: cnCoord,
                      cy: cyCoord,
                      stroke,
                      strokeLinecap,
                      strokeLinejoin,
                      strokeWidth,
                      dataKey,
                    } = props;
                    return (
                      <Dot
                        className={cn(
                          "stroke-white dark:stroke-gray-950",
                          onValueChange ? "cursor-pointer" : "",
                          getColorClassName(
                            categoryColors.get(dataKey) as AvailableChartColorsKeys,
                            "fill",
                          ),
                        )}
                        cx={cnCoord}
                        cy={cyCoord}
                        r={5}
                        fill=""
                        stroke={stroke}
                        strokeLinecap={strokeLinecap}
                        strokeLinejoin={strokeLinejoin}
                        strokeWidth={strokeWidth}
                        onClick={(_, event) => onDotClick(props, event)}
                      />
                    );
                  }}
                  dot={(props: any) => {
                    const {
                      stroke,
                      strokeLinecap,
                      strokeLinejoin,
                      strokeWidth,
                      cn: cnCoord,
                      cy: cyCoord,
                      dataKey,
                      index,
                    } = props;

                    if (
                      (hasOnlyOneValueForKey(data, category) &&
                        !(activeDot || (activeLegend && activeLegend !== category))) ||
                      (activeDot?.index === index && activeDot?.dataKey === category)
                    ) {
                      return (
                        <Dot
                          key={index}
                          cx={cnCoord}
                          cy={cyCoord}
                          r={5}
                          stroke={stroke}
                          fill=""
                          strokeLinecap={strokeLinecap}
                          strokeLinejoin={strokeLinejoin}
                          strokeWidth={strokeWidth}
                          className={cn(
                            "stroke-white dark:stroke-gray-950",
                            onValueChange ? "cursor-pointer" : "",
                            getColorClassName(
                              categoryColors.get(dataKey) as AvailableChartColorsKeys,
                              "fill",
                            ),
                          )}
                        />
                      );
                    }
                    return <React.Fragment key={index}></React.Fragment>;
                  }}
                  key={category}
                  name={category}
                  type="linear"
                  dataKey={category}
                  stroke=""
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  isAnimationActive={false}
                  connectNulls={connectNulls}
                  stackId={stacked ? "stack" : undefined}
                  fill={`url(#${categoryId})`}
                />
              </React.Fragment>
            );
          })}
          {/* hidden lines to increase clickable target area */}
          {onValueChange
            ? categories.map((category) => (
                <Line
                  className={cn("cursor-pointer")}
                  strokeOpacity={0}
                  key={category}
                  name={category}
                  type="linear"
                  dataKey={category}
                  stroke="transparent"
                  fill="transparent"
                  legendType="none"
                  tooltipType="none"
                  strokeWidth={12}
                  connectNulls={connectNulls}
                  onClick={(props: any, event) => {
                    event.stopPropagation();
                    const { name } = props;
                    onCategoryClick(name);
                  }}
                />
              ))
            : null}
        </RechartsAreaChart>
      </ResponsiveContainer>
    </div>
  );
});

AreaChart.displayName = "AreaChart";

export { AreaChart, type AreaChartEventProps, type TooltipProps };
